import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// Material
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import SwipeableDrawer from "@material-ui/core/SwipeableDrawer";
import CloseIcon from "@material-ui/icons/Close";

// Config
import { urls, getRoute, externalRoutes } from "../../config/urls";
import colors from "../../config/colors";

// Language
import { getTextSingle, language } from "../../languages/Languages";
import { getText } from "../../utils/textUtils";

// Components
import { userTypes } from "../../utils/UserTypes";
import LinkScroll from "../LinkScroll/LinkScroll";
import Text from "../Text/Text";
import SocialLinks from "../SocialLinks/SocialLinks";

const useStyles = makeStyles(theme => ({
  root: {
    "& .MuiDrawer-paperAnchorLeft": {
      right: 0,
      maxWidth: "100vw",
      display: "flex",
      justifyContent: "space-between",
      padding: "0 5%",
      paddingBottom: theme.spacing(6),
      background: theme.palette.background.default
    }
  },
  drawerTop: {
    height: 70,
    minHeight: 70,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center"
  },
  closeButton: {
    color: theme.palette.secondary.main,
    borderRadius: 4,
    fontSize: 20,
    padding: "4px 8px"
  },
  optionList: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(2)
  },
  mobileRoute: {
    fontSize: 24,
    fontFamily: "Gibson",
    fontWeight: "bold",
    marginBottom: theme.spacing(1)
  },
  mobileRouteLesser: {
    fontSize: 18,
    fontFamily: ["Montserrat", "sans-serif"].join(","),

    marginTop: theme.spacing(2)
  },
  draweTextDivision: {
    fontSize: 18,
    fontFamily: ["Montserrat", "sans-serif"].join(","),

    fontWeight: "bold",
    color: theme.palette.secondary.main
  },
  draweTextDivisionTop: {
    marginTop: theme.spacing(4)
  },
  selectedLine: {
    width: "0%",
    height: 3,
    backgroundColor: theme.palette.secondary.main,
    "&.selected": {
      width: "35%",
      transition: "width 0.3s"
    }
  },
  innerFooter: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginTop: theme.spacing(2),
    "& .MuiTypography-root": {
      fontFamily: "Maven Pro",
      fontSize: 14
    }
  },
  navigation: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: "80px"
  },
  loginWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(4),
    marginTop: theme.spacing(2),
    width: "100%"
  },
  loginButton: {
    fontSize: 16,
    textTransform: "none",
    boxShadow: "none",
    fontWeight: 700,
    color: colors.white,
    maxWidth: "348px",
    width: "100%",
    padding: "20px 0"
  },
  publicLoginButton: {
    backgroundColor: colors.lightBlue,
    "&:hover": {
      backgroundColor: colors.lightBlue
    }
  },
  policeLoginButton: {
    backgroundColor: colors.riverBed,
    "&:hover": {
      backgroundColor: colors.riverBed
    }
  }
}));

const MobileDrawer = ({ isOpened, onClose, anchor = "left", ...rest }) => {
  const [opened, setOpened] = useState(isOpened);
  const { pathname, hash } = useLocation();
  const classes = useStyles();
  let lastTimeout = null;
  useEffect(() => {
    setOpened(isOpened);
  }, [isOpened]);

  const onToogle =
    (toggle, isQuick = false) =>
    () => {
      if (lastTimeout) {
        clearTimeout(lastTimeout);
      }
      lastTimeout = setTimeout(
        () => {
          setOpened(toggle);
          if (!toggle && onClose) onClose();
        },
        isQuick ? 0 : 250
      );
    };

  const renderRoute = (text, url, isLesser, sectionId) => {
    const destination = sectionId ? `${url}#${sectionId}` : url;

    const isSelected =
      pathname !== "/" &&
      url.includes(pathname) &&
      (!sectionId || hash.includes(sectionId));

    const selectedClass = isSelected ? "selected" : "";

    return (
      <>
        <LinkScroll to={destination} onClick={onToogle(false)}>
          <Text
            className={
              isLesser ? classes.mobileRouteLesser : classes.mobileRoute
            }
          >
            {getText(text) || text}
          </Text>
        </LinkScroll>
        <div className={`${classes.selectedLine} ${selectedClass}`} />
      </>
    );
  };

  return (
    <SwipeableDrawer
      {...rest}
      anchor={anchor}
      open={opened}
      onClose={onToogle(false)}
      onOpen={onToogle(false)}
      className={classes.root}
    >
      <div>
        <div className={classes.drawerTop}>
          <LinkScroll
            className={classes.headerIcon}
            to={`${urls.home}#pageStart`}
            onClick={onToogle(false)}
          >
            <Text className={classes.draweTextDivision}>
              {getText(language.header.mobile.menu)}
            </Text>
          </LinkScroll>
          <IconButton
            className={classes.closeButton}
            onClick={onToogle(false, true)}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <List className={classes.optionList}>
          {renderRoute(
            "Get Connected",
            `${getRoute(urls.getConnected)}`,
            false,
            "pageStart"
          )}
          {renderRoute(
            language.pages.contactSupport.intro.title,
            `${getRoute(urls.contactSupport)}`,
            false,
            "pageStart"
          )}
          {renderRoute(language.pages.faq.intro.title, urls.faqs, true)}
          {renderRoute(
            language.pages.victimSupport.intro.title,
            getRoute(urls.victimSupport),
            true
          )}
          {renderRoute(
            language.pages.termsAndConditions.intro.title,
            `${getRoute(urls.termsAndConditions, {
              userType: userTypes.relatives
            })}`,
            true,
            "pageStart"
          )}
          {renderRoute(
            language.pages.termsAndConditions.privacy.title,
            `${getRoute(urls.termsAndConditions, {
              userType: userTypes.relatives
            })}`,
            true,
            "policies"
          )}
          {renderRoute(
            language.pages.cookiePolicy.title,
            `${getRoute(urls.cookie)}`,
            true,
            "pageStart"
          )}
          {renderRoute(
            language.pages.security.title,
            `${getRoute(urls.security)}`,
            true,
            "pageStart"
          )}
          {renderRoute(
            language.pages.pressRelease.title,
            `${getRoute(urls.pressReleases)}`,
            true,
            "pageStart"
          )}
        </List>
      </div>
      <nav className={classes.navigation}>
        <Text className={classes.mobileRoute}>
          {getTextSingle(language.common.login)}:
        </Text>
        <div className={classes.loginWrapper}>
          <Button
            className={`${classes.loginButton} ${classes.publicLoginButton}`}
            variant="contained"
            component="a"
            href={externalRoutes.webapp}
            rel="noopener noreferrer"
            target="_blank"
          >
            {getText(language.common.publicPlatform)}
          </Button>
          <Button
            className={`${classes.loginButton} ${classes.policeLoginButton}`}
            variant="contained"
            component="a"
            href={externalRoutes.policeapp}
            rel="noopener noreferrer"
            target="_blank"
          >
            {getText(language.common.policePlatform)}
          </Button>
        </div>
        <SocialLinks />
        <div className={classes.innerFooter}>
          <Text>{getText(language.footer.madeWith.with)}</Text>
        </div>
      </nav>
    </SwipeableDrawer>
  );
};

export default MobileDrawer;
